/**
 * Edit Club - Platform admin form for editing existing club information
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../../services/api';
import {
  Button,
  FormInput,
  FormSelect,
  FormTextArea,
  FormCheckbox,
  AdminPageLayout,
  Section,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Grid,
  Col
} from '../../components';
import {
  AttributeGrid,
  AttributeStack,
  FormActionGroup,
  SuccessHero
} from '../../components/admin/patterns';
import { CheckCircle, AlertTriangle } from 'lucide-react';

const EditClub = () => {
  const navigate = useNavigate();
  const { id: club_id } = useParams();

  const [formData, setFormData] = useState({
    // Basic club information
    legal_name: '',
    display_name: '',
    slug: '',
    custom_domain: '',
    club_type: 'soccer_club',
    description: '',
    website: '',
    email: '',
    phone: '',

    // Physical address
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'US',

    // Business information for invoicing
    business_registration_number: '',
    tax_id: '',
    vat_number: '',

    // Billing address (optional, defaults to physical)
    billing_address_line1: '',
    billing_address_line2: '',
    billing_city: '',
    billing_state: '',
    billing_zip_code: '',
    billing_country: 'US',
    same_as_physical: true,

    // Financial settings
    preferred_payment_method: 'bank_transfer',
    payment_terms: 30,
    currency: 'USD',
    timezone: 'America/New_York',

    // Club Settings - Age & Team Policy
    min_age_allowed: 4,
    max_age_allowed: 20,
    default_team_capacity: 20,

    // Admin password reset
    reset_admin_password: false
  });

  const [originalData, setOriginalData] = useState(null);
  const [adminUserEmail, setAdminUserEmail] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [passwordCopied, setPasswordCopied] = useState(false);

  useEffect(() => {
    loadClubData();
  }, [club_id]);

  const loadClubData = async () => {
    try {
      setLoading(true);
      const clubData = await apiService.getClub(club_id);

      // Map the API response to form data structure
      const mappedData = {
        legal_name: clubData.legal_name || '',
        display_name: clubData.display_name || '',
        slug: clubData.slug || '',
        custom_domain: clubData.custom_domain || '',
        club_type: clubData.club_type || 'soccer_club',
        description: clubData.description || '',
        website: clubData.website || '',
        email: clubData.contact_email || '',
        phone: clubData.contact_phone || '',

        address_line1: clubData.address_line1 || '',
        address_line2: clubData.address_line2 || '',
        city: clubData.city || '',
        state: clubData.state || '',
        zip_code: clubData.zip_code || '',
        country: clubData.country || 'US',

        business_registration_number: clubData.business_registration_number || '',
        tax_id: clubData.tax_id || '',
        vat_number: clubData.vat_number || '',

        billing_address_line1: clubData.billing_address_line1 || '',
        billing_address_line2: clubData.billing_address_line2 || '',
        billing_city: clubData.billing_city || '',
        billing_state: clubData.billing_state || '',
        billing_zip_code: clubData.billing_zip_code || '',
        billing_country: clubData.billing_country || 'US',
        same_as_physical: !clubData.billing_address_line1,

        preferred_payment_method: clubData.preferred_payment_method || 'bank_transfer',
        payment_terms: clubData.payment_terms || 30,
        currency: clubData.currency || 'USD',
        timezone: clubData.timezone || 'America/New_York',

        // Club Settings - defaults
        min_age_allowed: 4,
        max_age_allowed: 20,
        default_team_capacity: 20
      };

      // Load club settings separately
      try {
        const clubSettings = await apiService.getClubSettings(club_id);
        if (clubSettings) {
          mappedData.min_age_allowed = clubSettings.min_age_allowed || 4;
          mappedData.max_age_allowed = clubSettings.max_age_allowed || 20;
          mappedData.default_team_capacity = clubSettings.default_team_capacity || 20;
        }
      } catch (settingsErr) {
        console.warn('Could not fetch club settings:', settingsErr);
        // Use defaults already set above
      }

      setFormData(mappedData);
      setOriginalData(clubData);

      // Fetch admin user email if available
      if (clubData.admin_user_id) {
        try {
          const adminUser = await apiService.getUser(clubData.admin_user_id);
          setAdminUserEmail(adminUser.email);
        } catch (adminErr) {
          console.warn('Could not fetch admin user email:', adminErr);
        }
      }

      setLoading(false);

    } catch (err) {
      setErrors({
        load: err.message || 'Failed to load club data. Please try again.'
      });
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: newValue
      };

      // Handle billing address same as physical
      if (name === 'same_as_physical' && newValue) {
        updated.billing_address_line1 = updated.address_line1;
        updated.billing_address_line2 = updated.address_line2;
        updated.billing_city = updated.city;
        updated.billing_state = updated.state;
        updated.billing_zip_code = updated.zip_code;
        updated.billing_country = updated.country;
      } else if (name === 'same_as_physical' && !newValue) {
        // Clear billing address when unchecking
        updated.billing_address_line1 = '';
        updated.billing_address_line2 = '';
        updated.billing_city = '';
        updated.billing_state = '';
        updated.billing_zip_code = '';
        updated.billing_country = 'US';
      }

      return updated;
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Basic club information validation
    if (!formData.legal_name.trim()) newErrors.legal_name = 'Legal name is required';
    if (!formData.display_name.trim()) newErrors.display_name = 'Display name is required';
    if (!formData.club_type) newErrors.club_type = 'Club type is required';
    if (!formData.email.trim()) newErrors.email = 'Contact email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Contact phone is required';

    // Physical address validation
    if (!formData.address_line1.trim()) newErrors.address_line1 = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.zip_code.trim()) newErrors.zip_code = 'ZIP code is required';

    // Billing address validation (if different from physical)
    if (!formData.same_as_physical) {
      if (!formData.billing_address_line1.trim()) newErrors.billing_address_line1 = 'Billing address is required';
      if (!formData.billing_city.trim()) newErrors.billing_city = 'Billing city is required';
      if (!formData.billing_state.trim()) newErrors.billing_state = 'Billing state is required';
      if (!formData.billing_zip_code.trim()) newErrors.billing_zip_code = 'Billing ZIP code is required';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Payment terms validation
    if (formData.payment_terms && (formData.payment_terms < 1 || formData.payment_terms > 365)) {
      newErrors.payment_terms = 'Payment terms must be between 1 and 365 days';
    }

    // Club settings validation
    const minAge = parseInt(formData.min_age_allowed);
    const maxAge = parseInt(formData.max_age_allowed);
    const capacity = parseInt(formData.default_team_capacity);

    if (isNaN(minAge) || minAge < 4 || minAge > 25) {
      newErrors.min_age_allowed = 'Minimum age must be between 4 and 25';
    }
    if (isNaN(maxAge) || maxAge < 4 || maxAge > 25) {
      newErrors.max_age_allowed = 'Maximum age must be between 4 and 25';
    }
    if (minAge >= maxAge) {
      newErrors.min_age_allowed = 'Minimum age must be less than maximum age';
    }
    if (isNaN(capacity) || capacity < 5 || capacity > 100) {
      newErrors.default_team_capacity = 'Team capacity must be between 5 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const copyPasswordToClipboard = () => {
    // No longer needed - password reset now uses email links
    // Keeping function stub for backward compatibility
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setErrors({});
    setSuccess(null);
    setPasswordCopied(false);

    try {
      // Map form data to API format
      const updateData = {
        legal_name: formData.legal_name,
        display_name: formData.display_name,
        custom_domain: formData.custom_domain,
        club_type: formData.club_type,
        description: formData.description,
        website: formData.website,
        contact_email: formData.email,
        contact_phone: formData.phone,

        address_line1: formData.address_line1,
        address_line2: formData.address_line2,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        country: formData.country,

        business_registration_number: formData.business_registration_number,
        tax_id: formData.tax_id,
        vat_number: formData.vat_number,

        billing_address_line1: formData.same_as_physical ? formData.address_line1 : formData.billing_address_line1,
        billing_address_line2: formData.same_as_physical ? formData.address_line2 : formData.billing_address_line2,
        billing_city: formData.same_as_physical ? formData.city : formData.billing_city,
        billing_state: formData.same_as_physical ? formData.state : formData.billing_state,
        billing_zip_code: formData.same_as_physical ? formData.zip_code : formData.billing_zip_code,
        billing_country: formData.same_as_physical ? formData.country : formData.billing_country,

        preferred_payment_method: formData.preferred_payment_method,
        payment_terms: formData.payment_terms,
        currency: formData.currency,
        timezone: formData.timezone
      };

      // Update club information
      const result = await apiService.updateClub(club_id, updateData);

      // Update club settings separately
      let settingsMessage = '';
      try {
        const settingsData = {
          min_age_allowed: parseInt(formData.min_age_allowed),
          max_age_allowed: parseInt(formData.max_age_allowed),
          default_team_capacity: parseInt(formData.default_team_capacity)
        };
        await apiService.updateClubSettings(club_id, settingsData);
      } catch (settingsErr) {
        console.error('Failed to update club settings:', settingsErr);
        settingsMessage = ' However, club settings (age policy) could not be updated.';
      }

      // Handle admin password reset separately if requested
      let passwordResetMessage = '';

      if (formData.reset_admin_password) {
        if (!originalData?.admin_user_id) {
          passwordResetMessage = ' Warning: Could not find admin user ID. Password reset skipped.';
        } else {
          try {
            const passwordResult = await apiService.resetUserPassword(originalData.admin_user_id);

            // New token-based approach - no temp password returned
            if (passwordResult.email_sent === false) {
              passwordResetMessage = ' Password reset link generated. NOTE: Email could not be sent (email not configured). Password reset link has been generated but not delivered.';
            } else {
              passwordResetMessage = ' Password reset email sent successfully to administrator.';
            }
          } catch (passwordErr) {
            console.error('Password reset failed:', passwordErr);
            passwordResetMessage = ' However, password reset failed. Please try again from the user management page.';
          }
        }
      }

      setSuccess({
        message: 'Club information updated successfully!' + passwordResetMessage,
        club: result
      });

      // Reset password checkbox
      setFormData(prev => ({
        ...prev,
        reset_admin_password: false
      }));

    } catch (err) {
      setErrors({
        submit: err.message || 'Failed to update club. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="create-club">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading club information...</p>
        </div>
      </div>
    );
  }

  if (errors.load) {
    return (
      <div className="create-club">
        <div className="error-message">
          <h2>Error Loading Club</h2>
          <p>{errors.load}</p>
          <div style={{ marginTop: '20px' }}>
            <Button
              onClick={() => navigate('/admin/clubs')}
              variant="cancel"
            >
              Back to Club Management
            </Button>
            <Button
              onClick={loadClubData}
              variant="primary"
              style={{ marginLeft: '10px' }}
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdminPageLayout
      title={success ? "Changes Saved" : "Edit Club"}
      subtitle={success ? "Club settings have been updated" : `Settings for ${formData.display_name || formData.legal_name}`}
      backPath="/admin/clubs"
      maxWidth="1400px"
    >
      {success ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3.5rem' }}>
          <SuccessHero
            title="Club Updated"
            subtitle={`The settings for ${formData.legal_name} have been successfully updated.`}
            icon={<CheckCircle />}
            actions={
              <>
                <Button onClick={() => navigate('/admin/clubs')} variant="primary" size="lg">RETURN TO MANAGEMENT</Button>
                <Button onClick={() => setSuccess(null)} variant="outline" size="lg">CONTINUE MODIFICATION</Button>
              </>
            }
          />

          <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', marginTop: '1rem' }}>
            <Button onClick={() => navigate('/admin/clubs')} variant="primary" size="lg">Return to Management</Button>
            <Button onClick={() => setSuccess(null)} variant="outline" size="lg">Continue Modification</Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          {errors.submit && (
            <Section>
              <div style={{ padding: '1.25rem', background: 'var(--color-error-light)', border: '1px solid var(--color-error)', borderRadius: '12px', color: 'var(--color-error)', textAlign: 'center', fontWeight: 600 }}>
                UPDATE BLOCKED: {errors.submit}
              </div>
            </Section>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '3.5rem' }}>
            <Section title="CORE IDENTITY PROFILE">
              <Card level={1}>
                <CardContent>
                  <Grid columns={12} gap="2.5rem">
                    <Col span={6}>
                      <FormInput
                        label="Legal Business Entity Name"
                        name="legal_name"
                        value={formData.legal_name}
                        onChange={handleInputChange}
                        error={errors.legal_name}
                        required={true}
                        placeholder="Metropolitan Athletic Soccer Club"
                      />
                    </Col>
                    <Col span={6}>
                      <FormInput
                        label="Public Display Identifier"
                        name="display_name"
                        value={formData.display_name}
                        onChange={handleInputChange}
                        placeholder="Metro Soccer Academy"
                      />
                    </Col>

                    <Col span={6}>
                      <FormInput
                        label="Organization Slug (Locked)"
                        name="slug"
                        value={formData.slug}
                        disabled={true}
                        readOnly={true}
                        helpText="Primary directory identifier for system resolution."
                      />
                    </Col>
                    <Col span={6}>
                      <FormInput
                        label="Enterprise Custom Domain"
                        name="custom_domain"
                        value={formData.custom_domain}
                        onChange={handleInputChange}
                        placeholder="e.g., academy.com"
                        helpText="Map a secondary top-level gateway to this entity."
                      />
                    </Col>

                    <Col span={12}>
                      <FormTextArea
                        label="Mission Statement & Strategic Rationale"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={4}
                        placeholder="Outline the operational ethos and primary mission..."
                      />
                    </Col>

                    <Col span={4}>
                      <FormSelect
                        label="Governance Model"
                        name="club_type"
                        value={formData.club_type}
                        onChange={handleInputChange}
                        options={[
                          { value: 'soccer_club', label: 'Soccer Club' },
                          { value: 'youth_league', label: 'Youth League' },
                          { value: 'school_program', label: 'School Program' },
                          { value: 'academy', label: 'Academy' }
                        ]}
                      />
                    </Col>
                    <Col span={4}>
                      <FormInput label="Official Gateway (Website)" name="website" value={formData.website} onChange={handleInputChange} placeholder="https://www.club.com" />
                    </Col>
                    <Col span={4}>
                      <FormInput label="Administrative Contact Email" name="email" value={formData.email} onChange={handleInputChange} error={errors.email} required={true} placeholder="ops@club.com" />
                    </Col>
                  </Grid>
                </CardContent>
              </Card>
            </Section>

            <Section title="OPERATIONAL POLICIES & PARAMETERS">
              <Card level={1}>
                <CardContent>
                  <AttributeGrid columns={3}>
                    <FormInput
                      label="Minimum Participant Age"
                      type="number"
                      name="min_age_allowed"
                      value={formData.min_age_allowed}
                      onChange={handleInputChange}
                      error={errors.min_age_allowed}
                      helpText="Lower bounds for eligibility"
                    />
                    <FormInput
                      label="Maximum Participant Age"
                      type="number"
                      name="max_age_allowed"
                      value={formData.max_age_allowed}
                      onChange={handleInputChange}
                      error={errors.max_age_allowed}
                      helpText="Upper bounds for eligibility"
                    />
                    <FormInput
                      label="Default Team Capacity"
                      type="number"
                      name="default_team_capacity"
                      value={formData.default_team_capacity}
                      onChange={handleInputChange}
                      error={errors.default_team_capacity}
                      helpText="Standard roster limit configuration"
                    />
                  </AttributeGrid>
                </CardContent>
              </Card>
            </Section>

            <Grid columns={2} gap="2.5rem">
              <Section title="LOCALIZATION & TREASURY">
                <Card level={1} height="100%">
                  <CardContent>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                      <FormInput label="Physical Headquarters Address" name="address_line1" value={formData.address_line1} onChange={handleInputChange} error={errors.address_line1} required={true} />
                      <Grid columns={2} gap="1rem">
                        <FormInput label="City" name="city" value={formData.city} onChange={handleInputChange} />
                        <FormInput label="Territory / State" name="state" value={formData.state} onChange={handleInputChange} maxLength={2} />
                      </Grid>
                      <FormInput label="Postcode / ZIP" name="zip_code" value={formData.zip_code} onChange={handleInputChange} />

                      <div style={{ borderTop: '1px solid var(--color-border-light)', paddingTop: '2rem', marginTop: '0.5rem' }}>
                        <Grid columns={2} gap="1.5rem">
                          <FormInput label="Tax Identifier / EIN" name="tax_id" value={formData.tax_id} onChange={handleInputChange} />
                          <FormSelect
                            label="Settlement Terms"
                            name="payment_terms"
                            value={formData.payment_terms}
                            onChange={handleInputChange}
                            options={[
                              { value: 7, label: 'Standard Net 7' },
                              { value: 15, label: 'Standard Net 15' },
                              { value: 30, label: 'Standard Net 30' }
                            ]}
                          />
                        </Grid>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Section>

              <Section title="SECURITY & GUARDIANSHIP">
                <Card level={1} height="100%">
                  <CardContent>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                      {adminUserEmail && (
                        <AttributeStack
                          label="ASSIGNED ADMINISTRATOR"
                          value={adminUserEmail}
                          variant="large"
                          style={{
                            background: 'var(--surface-lvl2)',
                            padding: '1.5rem',
                            borderRadius: '12px',
                            border: '1px solid var(--color-border-light)'
                          }}
                        />
                      )}

                      <div style={{ background: 'var(--surface-lvl2)', padding: '1.75rem', borderRadius: '16px', border: '1px dashed var(--color-border)' }}>
                        <FormCheckbox
                          label="Initialize Security Credential Reset"
                          name="reset_admin_password"
                          checked={formData.reset_admin_password}
                          onChange={handleInputChange}
                        />
                        {formData.reset_admin_password && (
                          <div style={{ marginTop: '1.25rem', padding: '1rem', background: 'var(--color-warning-light)', borderRadius: '10px', fontSize: '0.9rem', color: 'var(--color-warning)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.75rem', border: '1px solid var(--color-warning)' }}>
                            <AlertTriangle size={18} />
                            <span>Protocol change will be finalized upon saving.</span>
                          </div>
                        )}
                        <p style={{ marginTop: '1rem', marginBottom: 0, fontSize: '0.85rem', opacity: 0.6 }}>
                          Forces a system-generated password update for the primary administrator node.
                        </p>
                      </div>

                      <div style={{ marginTop: 'auto', paddingTop: '2rem' }}>
                        <p style={{ fontSize: '0.85rem', opacity: 0.5, fontStyle: 'italic', textAlign: 'center' }}>
                          Last synchronized with registry on {new Date(originalData?.updated_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Section>
            </Grid>

            <FormActionGroup
              onCancel={() => navigate('/admin/clubs')}
              onSave={handleSubmit}
              loading={saving}
              saveLabel="Save Changes"
              cancelLabel="Cancel"
            />
          </div>
        </form>
      )}
    </AdminPageLayout>
  );
};

export default EditClub;